<template>
    <v-col cols="12" sm="6" lg="4" xl="3" class="pa-2">
        <article class="machine-card" role="button" tabindex="0" @click="$emit('open-item', item)"
            @keydown.enter="$emit('open-item', item)" @keydown.space.prevent="$emit('open-item', item)">
            <div class="machine-visual-panel">
                <MachineModelImage :size="112" :relativePath="item && item.ModelImage" />
                <div class="machine-status-stack">
                    <span class="status-pill" :class="item.IsActive ? 'success' : 'neutral'">
                        <v-icon size="14">{{ item.IsActive ? "mdi-check-circle" : "mdi-pause-circle" }}</v-icon>
                        {{ item.IsActive ? $t("text.active") : $t("text.inactive") }}
                    </span>
                    <span v-if="item.ServiceAgreement" class="status-pill agreement">
                        <v-icon size="14">mdi-file-document-check-outline</v-icon>
                        {{ $t("text.service_agreement") }}
                    </span>
                </div>
            </div>

            <div class="machine-card-body">
                <div class="machine-card-title-row">
                    <div class="machine-title-block">
                        <h3>{{ machineTitle(item) }}</h3>
                        <p>{{ machineSubtitle(item) }}</p>
                    </div>
                    <v-icon color="primary">mdi-chevron-right</v-icon>
                </div>

                <div class="machine-meta-grid">
                    <div class="machine-meta-item">
                        <span>{{ $t("text.facility") }}</span>
                        <strong>{{ item.Facility || "-" }}</strong>
                    </div>
                    <div class="machine-meta-item">
                        <span>{{ $t("text.serial_number") }}</span>
                        <strong>{{ item.SerialNumber || "-" }}</strong>
                    </div>
                    <div class="machine-meta-item">
                        <span>{{ $t("text.warrenty_expires") }}</span>
                        <strong>{{ formatDate(item.WarrantyExpiresDate) || "-" }}</strong>
                    </div>
                    <div class="machine-meta-item">
                        <span>{{ $t("text.qr_code") }}</span>
                        <strong>{{ item.Qrcode || "-" }}</strong>
                    </div>
                </div>

                <div class="machine-signal-row">
                    <span class="signal-chip service" :class="{ active: hasJobs(item.ActiveServiceJobs) }">
                        <v-icon icon="mdi-briefcase-outline" size="15"></v-icon>
                        {{ item.ActiveServiceJobs || 0 }} service
                    </span>
                    <span class="signal-chip parts" :class="{ active: hasJobs(item.ActivePartsJobs) }">
                        <v-icon icon="mdi-tag" size="15"></v-icon>
                        {{ item.ActivePartsJobs || 0 }} parts
                    </span>
                    <span v-if="item.ServiceAgreementNextDate" class="signal-chip date">
                        <v-icon size="15">mdi-calendar-clock</v-icon>
                        {{ formatDate(item.ServiceAgreementNextDate) }}
                    </span>
                </div>

                <div v-if="showPrice" class="machine-cost-grid">
                    <button type="button" class="cost-link" @click.stop="$emit('open-panel', item, 'ServicePanel')">
                        <span>{{ $t("text.Service") }}</span>
                        <strong>{{ formatAmount(item.TotalServiceAmount) }}</strong>
                        <v-icon size="18">mdi-chevron-right</v-icon>
                    </button>
                    <button type="button" class="cost-link" @click.stop="$emit('open-panel', item, 'PartsPanel')">
                        <span>{{ $t("text.Parts") }}</span>
                        <strong>{{ formatAmount(item.TotalPartsAmount) }}</strong>
                        <v-icon size="18">mdi-chevron-right</v-icon>
                    </button>
                </div>
            </div>
        </article>
    </v-col>
</template>


<script>
import MachineModelImage from "@/components/Cards/MachineModelImage.vue";

export default {
    name: "CustomerMachineCardItem",
    components: {
        MachineModelImage,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
        showPrice: {
            type: Boolean,
            default: false,
        },
        machineTitle: {
            type: Function,
            required: true,
        },
        machineSubtitle: {
            type: Function,
            required: true,
        },
        formatDate: {
            type: Function,
            required: true,
        },
        formatAmount: {
            type: Function,
            required: true,
        },
    },

    methods: {
        hasJobs(jobCount) {
            return Number(jobCount) > 0;
        },
    },

    computed: {},

    watch: {},
};
</script>


<style lang="scss" scoped>
.machine-card {
    width: 100%;
    display: flex;
    min-height: 100%;
    overflow: hidden;
    flex-direction: column;
    border: 1px solid rgba(24, 39, 75, 0.1);
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 22px rgba(24, 39, 75, 0.08);
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(30, 116, 92, 0.35);
        box-shadow: 0 12px 30px rgba(24, 39, 75, 0.14);
        outline: none;
        transform: translateY(-1px);
    }
}

.machine-visual-panel {
    position: relative;
    display: flex;
    justify-content: center;
    min-height: 150px;
    padding: 22px 16px 16px;
    border-bottom: 1px solid rgba(24, 39, 75, 0.08);
    background: #f2f6f4;
}

.machine-status-stack {
    position: absolute;
    top: 10px;
    left: 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    max-width: calc(100% - 20px);
}

.status-pill,
.signal-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 650;
    line-height: 1.2;
    white-space: nowrap;
}

.status-pill {
    padding: 5px 8px;
    border: 1px solid rgba(24, 39, 75, 0.1);
    background: rgba(255, 255, 255, 0.9);
    color: rgba(25, 34, 46, 0.78);

    &.success {
        border-color: rgba(46, 125, 50, 0.2);
        color: #2e7d32;
    }

    &.agreement {
        border-color: rgba(19, 105, 160, 0.2);
        color: #1369a0;
    }
}

.machine-card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
}

.machine-card-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.machine-title-block {
    min-width: 0;

    h3 {
        display: -webkit-box;
        overflow: hidden;
        margin: 0;
        color: rgba(25, 34, 46, 0.92);
        font-size: 16px;
        font-weight: 700;
        line-height: 1.32;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
    }

    p {
        overflow: hidden;
        margin: 5px 0 0;
        color: rgba(25, 34, 46, 0.58);
        font-size: 13px;
        line-height: 1.35;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.machine-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.machine-meta-item {
    min-width: 0;
    padding: 9px 10px;
    border: 1px solid rgba(24, 39, 75, 0.08);
    border-radius: 8px;
    background: #fbfcfd;

    span,
    strong {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    span {
        color: rgba(25, 34, 46, 0.5);
        font-size: 11px;
        font-weight: 650;
        text-transform: uppercase;
    }

    strong {
        margin-top: 3px;
        color: rgba(25, 34, 46, 0.86);
        font-size: 13px;
        font-weight: 650;
    }
}

.machine-signal-row {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
}

.signal-chip {
    min-height: 28px;
    padding: 5px 8px;
    background: #f4f6f8;
    color: rgba(25, 34, 46, 0.62);

    &.service.active {
        background: rgba(120, 86, 255, 0.11);
        color: #5a42b3;
    }

    &.parts.active {
        background: rgba(204, 75, 121, 0.12);
        color: #9a3158;
    }

    &.date {
        background: rgba(225, 137, 50, 0.13);
        color: #8a541f;
    }
}

.machine-cost-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: auto;
}

.cost-link {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 6px;
    min-width: 0;
    min-height: 38px;
    padding: 8px 10px;
    border: 1px solid rgba(24, 39, 75, 0.1);
    border-radius: 8px;
    background: #ffffff;
    color: rgba(25, 34, 46, 0.84);
    font: inherit;
    text-align: left;
    cursor: pointer;

    span,
    strong {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    span {
        color: rgba(25, 34, 46, 0.58);
        font-size: 12px;
        font-weight: 650;
    }

    strong {
        color: rgba(25, 34, 46, 0.9);
        font-size: 13px;
        font-weight: 750;
    }

    &:hover,
    &:focus-visible {
        border-color: rgba(30, 116, 92, 0.35);
        outline: none;
    }
}

@media (max-width: 720px) {

    .machine-meta-grid,
    .machine-cost-grid {
        grid-template-columns: 1fr;
    }
}
</style>
